import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import { useApi } from '../../../../../hooks/useApi';
import { ActionButtons } from '../../../../../globalComponents/modal/ActionButtons';
import { useCompanyDispatch} from '../../../../../stateProviders/CompanyStateProvider';
import { Modal } from '../../../../../globalComponents/modal/Modal';

export const AddAdditionalCostModal: React.FC<{
    companyId: string;
    gridRef: React.MutableRefObject<any>;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ companyId, gridRef, modalOpen, setModalOpen }) => {
    const api = useApi();
    const companyDispatch = useCompanyDispatch();
    const [cost, setCost] = useState({
        costName: '',
        costDescription: '',
        costAmount: '',
    });

    const resetForm = () => {
        setCost({
            costName: '',
            costDescription: '',
            costAmount: '',
        });
    };

    useEffect(() => {
        if (modalOpen) {
            resetForm();
        }
    }, [modalOpen]);
    return (
        <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            modalTitle={
                <Typography variant="h2" fontWeight="bold">
                    Add Additional Cost
                </Typography>
            }
            modalBody={
                <Stack spacing={2} sx={{ mb: 2 }}>
                    {[{
                        label: 'Cost Name',
                        name: 'costName',
                        placeholder: 'Example: Admin Fees',
                        type: 'text',
                        required: true,
                        value: cost.costName,
                    }, {
                        label: 'Cost Amount ($)',
                        name: 'costAmount',
                        placeholder: 'Enter amount',
                        type: 'number',
                        required: true,
                        value: cost.costAmount,
                    }, {
                        label: 'Cost Description',
                        name: 'costDescription',
                        placeholder: 'Example: Estimated misc admin fees',
                        type: 'text',
                        required: false,
                        value: cost.costDescription,
                    }].map((field) => (
                        <Stack key={field.name}>
                            <FormLabel component="legend" sx={{ mb: 1 }} required={field.required}>
                                {field.label}
                            </FormLabel>
                            <TextField
                                fullWidth
                                name={field.name}
                                type={field.type}
                                placeholder={field.placeholder}
                                value={field.value}
                                slotProps={field.type === 'number' ? {
                                    htmlInput: { min: 0, step: 0.01 }
                                } : undefined}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    // Allow clearing number fields completely
                                    if (field.type === 'number' && value === '') {
                                        value = '';
                                    }
                                    setCost({
                                        ...cost,
                                        [field.name]: value,
                                    });
                                }}
                            />
                        </Stack>
                    ))}
                </Stack>
            }
            modalFooter={
                <ActionButtons
                    confirmButtonText="Add"
                    confirmCallback={async () => {
                        await api.additionalCost.create({
                            companyId,
                            costName: cost.costName,
                            costAmount: parseFloat(cost.costAmount) || 0,
                            costDescription: cost.costDescription
                        });
                        const additionalCosts = await api.additionalCost.search({ companyId });
                        companyDispatch({ additionalCosts });
                        gridRef.current?.onRefresh();
                        resetForm();
                        setModalOpen(false);
                    }}
                    confirmDisabled={
                        cost.costName.trim() === '' ||
                        cost.costAmount.trim() === '' ||
                        Number(cost.costAmount) <= 0 ||
                        isNaN(Number(cost.costAmount))
                    }
                    confirmButtonStyle="success"
                    cancelCallback={() => setModalOpen(false)}
                />
            }
        />
    );
};
